import { useState, useEffect } from 'react'
import { Cloud, HardDrive, Box, Link2, Unlink, Loader2 } from 'lucide-react'
import api from '../../core/services/api'

interface Provider {
  provider: string
  name: string
  connected: boolean
  account_name?: string
  icon: string
}

const ICONS: Record<string, typeof Cloud> = {
  cloud: Cloud,
  'hard-drive': HardDrive,
  box: Box,
}

interface Props {
  onToast: (msg: string, type: 'success' | 'error') => void
}

export default function CloudConnectionsPanel({ onToast }: Props) {
  const [providers, setProviders] = useState<Provider[]>([])
  const [loading, setLoading] = useState(true)
  const [disconnecting, setDisconnecting] = useState<string | null>(null)

  useEffect(() => {
    loadProviders()

    // Controlla URL params per feedback da callback OAuth
    const params = new URLSearchParams(window.location.search)
    const connected = params.get('cloud_connected')
    const error = params.get('cloud_error')
    if (connected) {
      onToast(`${connected} collegato con successo`, 'success')
      window.history.replaceState({}, '', window.location.pathname)
    }
    if (error) {
      onToast(`Errore collegamento ${error}`, 'error')
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const loadProviders = async () => {
    try {
      const resp = await api.get('/v1/cloud/providers')
      setProviders(resp.data)
    } catch {
      // Provider non configurati, non mostrare errore
    } finally {
      setLoading(false)
    }
  }

  const connect = async (provider: string) => {
    try {
      const resp = await api.get(`/v1/cloud/${provider}/auth-url`)
      window.location.href = resp.data.auth_url
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore', 'error')
    }
  }

  const disconnect = async (provider: string) => {
    if (!confirm(`Disconnettere ${provider}?`)) return
    setDisconnecting(provider)
    try {
      await api.delete(`/v1/cloud/${provider}/disconnect`)
      onToast('Disconnesso', 'success')
      await loadProviders()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore', 'error')
    } finally {
      setDisconnecting(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    )
  }

  if (providers.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-4">
        Nessun provider cloud configurato dall'amministratore.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Collega i tuoi account cloud per importare ed esportare file direttamente dal gestionale.
      </p>

      {providers.map(p => {
        const Icon = ICONS[p.icon] || Cloud
        return (
          <div
            key={p.provider}
            className="flex items-center gap-4 p-4 rounded-lg border border-gray-200"
          >
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-gray-600" />
            </div>

            <div className="flex-1">
              <p className="font-medium text-gray-900">{p.name}</p>
              {p.connected ? (
                <p className="text-xs text-green-600">{p.account_name}</p>
              ) : (
                <p className="text-xs text-gray-400">Non collegato</p>
              )}
            </div>

            {p.connected ? (
              <button
                onClick={() => disconnect(p.provider)}
                disabled={disconnecting === p.provider}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                {disconnecting === p.provider ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Unlink className="w-3.5 h-3.5" />
                )}
                Disconnetti
              </button>
            ) : (
              <button
                onClick={() => connect(p.provider)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50"
              >
                <Link2 className="w-3.5 h-3.5" />
                Collega
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
